import React, { useState, useRef, useEffect } from "react";
import CompanyMetricCard from "./CompanyMetricCard";
import { DEFAULT_VISUAL_CONFIG, APPS, FROSTED_PANEL_STYLE } from "./constants";
import ConfirmDialog from "./ConfirmDialog";
import LoadingScreen from "../LoadingScreen";
import { GET_IMAGE } from "../../helpers/images";
import { GMB_LOGO_URL } from "../../constants/branding";
import { getCompanyMetricChartData } from "../../hooks/kpi";
import { companyToday } from "../../helpers/companyTime";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

const COLORS = [
  { label: "Brand", value: "var(--gmb-primary)" },
  { label: "Emerald", value: "#10B981" },
  { label: "Rose", value: "#F43F5E" },
  { label: "Amber", value: "#F59E0B" },
  { label: "Sky", value: "#0EA5E9" },
  { label: "Slate", value: "#475569" },
  { label: "Orange", value: "#F97316" },
];

const VIS_TYPES = [
  { id: "number", label: "Number", preview: (<span className="text-[13px] font-semibold leading-none">1,240</span>) },
  { id: "sparkline", label: "Spark", preview: (<svg viewBox="0 0 40 20" className="w-[38px] h-[20px]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 14 L9 9 L15 12 L22 5 L30 10 L38 6"/></svg>) },
  { id: "line", label: "Line", preview: (<svg viewBox="0 0 40 22" className="w-[38px] h-[22px]"><path d="M2 2 V20 H38" fill="none" stroke="#cbd5e1" strokeWidth="1"/><path d="M4 15 L13 9 L21 13 L36 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
  { id: "area", label: "Area", preview: (<svg viewBox="0 0 40 22" className="w-[38px] h-[22px]"><path d="M2 15 L12 8 L22 12 L38 5 L38 20 L2 20 Z" fill="currentColor" fillOpacity="0.22"/><path d="M2 15 L12 8 L22 12 L38 5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>) },
  { id: "bar", label: "Bar", preview: (<svg viewBox="0 0 40 22" className="w-[38px] h-[22px]" fill="currentColor"><rect x="4" y="11" width="6" height="9" rx="1"/><rect x="14" y="5" width="6" height="15" rx="1"/><rect x="24" y="9" width="6" height="11" rx="1"/><rect x="34" y="14" width="4" height="6" rx="1"/></svg>) },
  { id: "progress", label: "Progress", preview: (<svg viewBox="0 0 40 22" className="w-[38px] h-[22px]"><rect x="3" y="8" width="34" height="7" rx="3.5" fill="#e2e8f0"/><rect x="3" y="8" width="22" height="7" rx="3.5" fill="currentColor"/></svg>) },
  { id: "radial", label: "Ring", preview: (<svg viewBox="0 0 24 24" className="w-[20px] h-[20px]" fill="none"><circle cx="12" cy="12" r="8" stroke="#e2e8f0" strokeWidth="3"/><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="3" strokeDasharray="34 50" strokeLinecap="round" transform="rotate(-90 12 12)"/></svg>) },
  { id: "thermometer", label: "Goal", preview: (<svg viewBox="0 0 40 22" className="w-[38px] h-[22px]" fill="none"><path d="M4 18 A12 12 0 0 1 36 18" stroke="#e2e8f0" strokeWidth="3" strokeLinecap="round"/><path d="M4 18 A12 12 0 0 1 22 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>) },
  { id: "text-only", label: "Text", preview: (<svg viewBox="0 0 40 22" className="w-[34px] h-[18px]" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M4 6 H36 M4 12 H36 M4 18 H24"/></svg>) },
];

/** Same values as ExploreCustomMetricsTypes / createCustomKpi optional.operand (add, subtract, multiply, divide) */
const CALCULATION_OPTIONS = [
  { value: "add", label: "Add +" },
  { value: "subtract", label: "Subtract −" },
  { value: "multiply", label: "Multiply ×" },
  { value: "divide", label: "Divide ÷" },
];

const PremiumRobotIcon = ({ className }) => (
  <div className={`robot-icon-container flex items-center justify-center animate-robot-entry animate-robot-rotate-slow ${className}`}>
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="robotBodyGradientModalSettings" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--gmb-primary)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--gmb-primary)" />
        </linearGradient>
        <filter id="robotGlowModalSettings" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>
      <rect x="6" y="5" width="12" height="9" rx="3.5" fill="url(#robotBodyGradientModalSettings)" />
      <rect x="7.5" y="6.5" width="9" height="6" rx="2" fill="white" fillOpacity="0.1" />
      <circle cx="10" cy="9.5" r="1.2" fill="white" filter="url(#robotGlowModalSettings)" />
      <circle cx="14" cy="9.5" r="1.2" fill="white" filter="url(#robotGlowModalSettings)" />
      <path d="M12 5V3M10 3h4" stroke="var(--gmb-primary)" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="5" y="15" width="14" height="5" rx="2.5" fill="url(#robotBodyGradientModalSettings)" />
      <rect x="7.5" y="16.5" width="10" height="2" rx="1" fill="white" fillOpacity="0.1" />
      <rect x="10.5" y="14" width="3" height="1.5" fill="var(--gmb-primary)" fillOpacity="0.8" />
    </svg>
  </div>
);

function getAppLogo(integrationApps, appId) {
  if (!Array.isArray(integrationApps) || !appId) return null;
  const app = integrationApps.find((a) => (a.code || a.id) === appId);
  return app?.logo ? GET_IMAGE(app.logo) || app.logo : null;
}

function MetricSettingsModal({
  metric,
  allMetrics,
  allWorkspaces,
  isGlobalView,
  onClose,
  onSave,
  initialTab = "appearance",
  onReopenWorkspaceSelector,
  activeWorkspace,
  integrationApps = [],
  companyScoresByDay,
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [visualConfig, setVisualConfig] = useState(() => {
    const fromMetric = metric.visualConfig || DEFAULT_VISUAL_CONFIG;
    const granularity = fromMetric.chartGranularity === "week" ? "week" : "day";
    return { ...DEFAULT_VISUAL_CONFIG, ...fromMetric, chartGranularity: granularity };
  });
  const [isGamified, setIsGamified] = useState(!!metric.gameConfig && metric.gameConfig.status === "live");
  const [gameStep, setGameStep] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAppearanceSuccess, setShowAppearanceSuccess] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [explainerVideo, setExplainerVideo] = useState(null);

  // This modal is only ever mounted while open (parent gates it with
  // `{settingsMetric && <MetricSettingsModal .../>}`), so being mounted at
  // all is the "open" signal — lock unconditionally, restore on unmount.
  useLockBodyScroll(true);

  const resolvedWorkspace = activeWorkspace || allWorkspaces.find((w) => w.id === metric.workspaceId);
  const isContextShifted = metric.assignedFromWorkspaceId === "GLOBAL";
  const isAlreadyLive = metric.gameConfig?.status === "live";

  const defaultGameConfig = {
    type: "progressive",
    status: "draft",
    inputMethod: metric.appId === "manual" ? "manual" : "connected",
    gameName: metric.name,
    dailyTarget: 100,
    pointsRule: { metricAmount: 1, pointsEarned: 10 },
    anchorValue: 50,
    anchorUnit: "percent",
    benchmarkPoints: 1000,
    pointsPerUnitAbove: 25,
    pointsPerUnitBelow: 25,
    effectType: "positive",
    valueType: "monetary",
    resetFrequency: "daily",
    manualConfig: { type: "form", fieldLabel: "Input value", required: true, notifyOnSubmission: true, formFields: [] },
    connectedConfig: { accountId: null, accounts: [{ id: "acc_1", name: "Primary account", identifier: "**** 4242", provider: metric.appId }] },
    compositeMetric: false,
    compositeConfig: { metricAId: "", operator: "add", metricBId: "" },
    monitorMode: false,
    scorekeeperOnly: false,
  };

  const normalizePointsRule = (pr) => {
    if (!pr || typeof pr !== "object") return { metricAmount: 1, pointsEarned: 10 };
    let metricAmount = 1;
    if (typeof pr.metricAmount === "number") metricAmount = pr.metricAmount;
    else if (typeof pr.pointScale === "number") metricAmount = pr.pointScale;
    let pointsEarned = 10;
    if (typeof pr.pointsEarned === "number") pointsEarned = pr.pointsEarned;
    else if (typeof pr.pointValue === "number") pointsEarned = pr.pointValue;
    return { metricAmount, pointsEarned };
  };

  const [gameConfig, setGameConfig] = useState(() => {
    const base = metric.gameConfig ? { ...defaultGameConfig, ...metric.gameConfig } : defaultGameConfig;
    const merged = { ...base, pointsRule: normalizePointsRule(base.pointsRule) };
    if (base.compositeConfig) merged.compositeConfig = { ...defaultGameConfig.compositeConfig, ...base.compositeConfig };
    // Load composite from metric.optional (same shape as ExploreCustomMetricsTypes createCustomKpi: sourceId1, sourceId2, operand)
    const opt = metric.optional;
    if (opt && (opt.sourceId1 || opt.sourceId2 || opt.operand)) {
      merged.compositeMetric = !!(opt.sourceId1 && opt.sourceId2);
      merged.compositeConfig = {
        metricAId: opt.sourceId1 ?? "",
        metricBId: opt.sourceId2 ?? "",
        operator: ["add", "subtract", "multiply", "divide"].includes(opt.operand) ? opt.operand : "add",
      };
    }
    return merged;
  });

  const gameConfigRef = useRef(gameConfig);
  const visualConfigRef = useRef(visualConfig);
  useEffect(() => {
    gameConfigRef.current = gameConfig;
  }, [gameConfig]);
  useEffect(() => {
    visualConfigRef.current = visualConfig;
  }, [visualConfig]);

  const workspaceMetrics = (() => {
    const wsId = resolvedWorkspace?.id || (metric.ownerWorkspaceId && metric.ownerWorkspaceId !== "GLOBAL" ? metric.ownerWorkspaceId : null) || metric.workspaceId;
    if (!wsId || wsId === "GLOBAL") return [];
    const currentId = metric.dashboardId || metric.id || metric._id;
    return allMetrics.filter(
      (m) =>
        (m.workspaceId === wsId || m.ownerWorkspaceId === wsId) &&
        (m.dashboardId || m.id) !== currentId
    );
  })();
  const isCompositeIncomplete = gameConfig.compositeMetric && (!gameConfig.compositeConfig?.metricAId || !gameConfig.compositeConfig?.metricBId);

  const updateCompositeConfig = (updates) => {
    setGameConfig((prev) => ({
      ...prev,
      compositeConfig: {
        metricAId: prev.compositeConfig?.metricAId ?? "",
        operator: prev.compositeConfig?.operator ?? "+",
        metricBId: prev.compositeConfig?.metricBId ?? "",
        ...updates,
      },
    }));
  };

  const handleSave = (newStatus, shouldClose = true) => {
    const latest = gameConfigRef.current;
    const status = newStatus || latest.status;
    let updatedGameConfig = isGamified ? { ...latest, status } : metric.gameConfig;
    if (isGamified && updatedGameConfig) {
      const pr = updatedGameConfig.pointsRule || {};
      const metricAmount = pr.metricAmount != null && pr.metricAmount !== "" ? Number(pr.metricAmount) : 1;
      const pointsEarned = pr.pointsEarned != null && pr.pointsEarned !== "" ? Number(pr.pointsEarned) : 10;
      updatedGameConfig = {
        ...updatedGameConfig,
        dailyTarget: updatedGameConfig.dailyTarget != null && updatedGameConfig.dailyTarget !== "" ? Number(updatedGameConfig.dailyTarget) : (metric.gameConfig?.dailyTarget ?? 100),
        pointsRule: { ...pr, metricAmount, pointsEarned },
      };
    }
    // Composite metric (Workspace Only): compositeConfig (metricAId, operator, metricBId) is passed through;
    // parent maps to top-level dataSourceOne, calculationMethod, dataSourceTwo when sending to server.
    const metricId = metric.id || metric._id || metric.dashboardId;
    const vc = visualConfigRef.current;
    const visual = {
      type: vc.type,
      chartGranularity: vc.chartGranularity === "week" ? "week" : "day",
      primaryColor: vc.primaryColor,
      gradientEnabled: vc.gradientEnabled,
      smoothLine: vc.smoothLine,
      chartOpacity: vc.chartOpacity,
    };
    return onSave(metricId, visual, updatedGameConfig, shouldClose);
  };

  const handleSaveComposite = () => {
    if (isCompositeIncomplete) return;
    handleSave(undefined, false);
  };

  const handleLaunchOrUpdate = async () => {
    if (isAlreadyLive) setShowUpdateConfirm(true);
    else {
      setIsFinalizing(true);
      try {
        const result = handleSave("live", true);
        await (result && typeof result.then === "function" ? result : Promise.resolve());
        setShowSuccess(true);
      } catch (_) {
        // Error already shown by parent; do not show success
      } finally {
        setIsFinalizing(false);
      }
    }
  };

  const finalizeLaunch = async () => {
    try {
      const result = handleSave("live", true);
      await (result && typeof result.then === "function" ? result : Promise.resolve());
      onClose();
    } catch (_) {
      // Error already shown by parent; modal stays open
    }
  };

  const updateVisualConfig = (updates) => setVisualConfig((prev) => ({ ...prev, ...updates }));

  const secondaryBtnClass = "px-[24px] py-[8px] bg-white border border-[var(--gmb-primary-tint)] rounded-lg font-medium text-sm text-gray-700 hover:bg-[var(--gmb-primary-soft)] hover:border-[var(--gmb-primary-tint)] transition-all";
  const primaryBtnClass = "px-[24px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-sm shadow-sm hover:brightness-95 transition-all disabled:opacity-40 disabled:pointer-events-none";
  const textBtnClass = "text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors";
  const inputBaseClass = "w-full bg-gray-50 border border-gray-200 px-5 py-3 rounded-lg font-normal text-gray-900 focus:border-[var(--gmb-primary)] focus:bg-white outline-none transition-all placeholder:text-gray-400 placeholder:font-light";

  // Assignments: metrics connected to workspaces via kpi.groupId === workspace._id (ownerWorkspaceId).
  // Company KPI: use ownerWorkspaceId; same logical metric = same id or same name+appId across workspaces.
  const assignments = (() => {
    if (metric._source === "companyKpi") {
      return allMetrics.filter(
        (m) =>
          m._source === "companyKpi" &&
          m.ownerWorkspaceId &&
          m.ownerWorkspaceId !== "GLOBAL" &&
          (m.id === metric.id || (m.name === metric.name && m.appId === metric.appId))
      );
    }
    return allMetrics.filter((m) => m.id === metric.id && m.workspaceId !== "GLOBAL");
  })();

  // Resolve workspace by groupId (ownerWorkspaceId) for company KPIs; by workspaceId otherwise
  const getWorkspaceForInstance = (instance) =>
    allWorkspaces.find((w) => w.id === (instance.ownerWorkspaceId || instance.workspaceId));

  const renderGamificationStatusPanel = () => (
    <div className="flex-grow flex flex-col overflow-y-auto bg-[#F9FBFC] px-10 py-12">
      <div className="max-w-4xl mx-auto w-full space-y-10">
        <header className="text-center">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M4.93 19.07a10 10 0 0 1 0-14.14M7.76 16.24a6 6 0 0 1 0-8.48M19.07 4.93a10 10 0 0 1 0 14.14M16.24 7.76a6 6 0 0 1 0 8.48"/><circle cx="12" cy="12" r="1.5"/></svg></div>
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Status & Insights</h3>
          <p className="text-sm text-gray-500 mt-2 max-w-lg mx-auto leading-relaxed font-normal">View how this metric is connected to workspaces (by KPI groupId) and configured across active workspaces.</p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <section className="bg-white p-8 rounded-xl border border-gray-50 shadow-sm flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-[11px] font-semibold text-gray-400 ">Assigned Workspaces</h4>
              <span className="bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] px-2 py-0.5 rounded text-[10px] font-medium">{assignments.length}</span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[300px] pr-2 custom-scrollbar">
              {assignments.length > 0
                ? assignments.map((instance) => {
                    const ws = getWorkspaceForInstance(instance);
                    return (
                      <div key={instance.dashboardId} className="flex items-center p-3 rounded-lg bg-gray-50 border border-gray-100 transition-all hover:bg-white hover:shadow-sm">
                        <div className="w-8 h-8 rounded-lg overflow-hidden mr-3 shrink-0 border border-white">
                          <img src={ws?.icon} alt={ws?.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-grow overflow-hidden min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{ws?.name || "Workspace"}</p>
                          <p className="text-[10px] text-gray-400 font-medium">Assigned</p>
                          {instance.name && (
                            <p className="text-[10px] text-[var(--gmb-primary)] font-medium truncate mt-0.5" title={instance.name}>
                              Connected: {instance.name}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })
                : (
                  <div className="py-10 text-center opacity-40">
                    <p className="text-xs font-medium text-gray-400 ">Not assigned yet</p>
                  </div>
                )}
            </div>
          </section>
          <section className="bg-white p-8 rounded-xl border border-gray-50 shadow-sm flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-[11px] font-semibold text-gray-400 ">Gamified In</h4>
              <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded text-[10px] font-medium">{assignments.filter((a) => a.gameConfig?.status === "live").length}</span>
            </div>
            <div className="space-y-3 flex-grow overflow-y-auto max-h-[300px] pr-2 custom-scrollbar">
              {assignments.some((a) => a.gameConfig) ? (
                assignments
                  .filter((a) => a.gameConfig)
                  .map((instance) => {
                    const ws = getWorkspaceForInstance(instance);
                    const isLive = instance.gameConfig?.status === "live";
                    return (
                      <div key={`game-${instance.dashboardId}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100 transition-all hover:bg-white hover:shadow-sm">
                        <div className="flex items-center overflow-hidden mr-4 min-w-0 flex-grow">
                          <span className="mr-3 shrink-0 text-gray-500">{instance.gameConfig?.type === "progressive" ? (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>) : (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>)}</span>
                          <div className="truncate min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{ws?.name || "Workspace"}</p>
                            <p className="text-[10px] text-gray-400 font-medium capitalize">{instance.gameConfig?.type} Challenge</p>
                            {instance.name && (
                              <p className="text-[10px] text-[var(--gmb-primary)] font-medium truncate mt-0.5" title={instance.name}>
                                Connected: {instance.name}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ml-2 ${isLive ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-gray-100 text-gray-400"}`}>{isLive ? "Active" : "Inactive"}</span>
                      </div>
                    );
                  })
              ) : (
                <div className="py-10 text-center opacity-40">
                  <p className="text-xs font-medium text-gray-400 ">No active games found</p>
                </div>
              )}
            </div>
          </section>
        </div>
        {metric.appId && metric.appId !== "manual" ? (
          <footer className="bg-[var(--gmb-primary-soft)] border border-[var(--gmb-primary-tint)] rounded-xl p-6">
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white border border-[var(--gmb-primary-tint)] shadow-sm shrink-0 p-2">
                <img
                  src={getAppLogo(integrationApps, metric.appId) || APPS.find((a) => a.id === metric.appId)?.icon || GMB_LOGO_URL}
                  alt=""
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center sm:text-left min-w-0">
                <h5 className="text-[11px] font-semibold text-[var(--gmb-primary)] mb-1">Connected to</h5>
                <p className="text-sm font-medium text-[var(--gmb-primary)] truncate">
                  {integrationApps.find((a) => (a.code || a.id) === metric.appId)?.name || APPS.find((a) => a.id === metric.appId)?.name || metric.appId}
                </p>
              </div>
            </div>
          </footer>
        ) : (
          <footer className="bg-[var(--gmb-primary-soft)] border border-[var(--gmb-primary-tint)] rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-3 text-[var(--gmb-primary)] mb-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-4 h-4">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4m0-4h.01" />
              </svg>
              <h5 className="text-sm font-semibold tracking-tight">Ownership & Scope</h5>
            </div>
            <p className="text-[12px] text-[var(--gmb-primary)] font-medium leading-relaxed max-w-xl mx-auto">
              Gamification rules and targets are configured at the <strong>workspace level</strong> by local admins. The company dashboard provides a high-level view of template usage and global performance visibility.
            </p>
          </footer>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-slate-900/15 backdrop-blur-[3px]" onClick={onClose} />

      <div
        className={`rounded-lg w-full relative z-10 flex flex-col overflow-hidden ${
          isFinalizing || showSuccess ? "max-w-[360px]" : "max-w-[660px] max-h-[85vh]"
        }`}
        style={FROSTED_PANEL_STYLE}
      >
        {isFinalizing && (
          <div className="relative bg-white flex flex-col items-center justify-center p-10 text-center gap-3">
            <LoadingScreen height="auto" logoHeight="160px" />
            <p className="text-gray-400 font-medium text-sm tracking-wide">
              {isAlreadyLive ? "Updating challenge..." : "Activating metric..."}
            </p>
          </div>
        )}

        {showSuccess && (
          <div className="relative bg-white flex flex-col items-center justify-center p-10 text-center">
            <div className="max-w-md w-full space-y-8">
              <div className="w-24 h-24 bg-[var(--gmb-primary-soft)] rounded-full flex items-center justify-center mx-auto shadow-inner text-[var(--gmb-primary)]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-12 h-12"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">Your game is live</h2>
                <p className="text-gray-500 font-normal">This metric is now active. Players can start earning points immediately.</p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button type="button" onClick={finalizeLaunch} className={primaryBtnClass + " w-full"}>View metric</button>
                <button type="button" onClick={onClose} className="text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors py-2">Close</button>
              </div>
            </div>
          </div>
        )}

        {showAppearanceSuccess && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-[160] flex items-center justify-center p-6">
            <div className="rounded-lg w-full max-w-[360px] px-[22px] pt-[22px] pb-[18px] text-center" style={FROSTED_PANEL_STYLE}>
              <div className="w-11 h-11 bg-emerald-50 text-emerald-500 rounded-[11px] flex items-center justify-center mx-auto mb-[14px]">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]"><path d="M20 6 9 17l-5-5"/></svg>
              </div>
              <h2 className="text-[16px] font-semibold text-gray-900 mb-[5px]">Appearance updated</h2>
              <p className="text-[13px] text-gray-500 leading-[1.5] mb-[18px] font-normal">Your team sees the updated card on the dashboard right away.</p>
              <div className="flex items-center justify-center gap-[10px]">
                <button type="button" onClick={() => setShowAppearanceSuccess(false)} className="whitespace-nowrap px-[20px] py-[8px] rounded-lg font-medium text-[13px] text-slate-600 border border-[rgba(15,23,42,0.1)] hover:bg-slate-50 transition-colors">Keep editing</button>
                <button type="button" onClick={onClose} className="whitespace-nowrap px-[20px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] shadow-sm hover:brightness-95 transition-all">Done</button>
              </div>
            </div>
          </div>
        )}

        {showUpdateConfirm && (
          <ConfirmDialog
            title="You're updating a live metric"
            message="These changes will apply immediately and may affect active players."
            confirmText="Update metric"
            onConfirm={async () => {
              setShowUpdateConfirm(false);
              setIsFinalizing(true);
              try {
                await finalizeLaunch();
              } finally {
                setIsFinalizing(false);
              }
            }}
            onCancel={() => setShowUpdateConfirm(false)}
          />
        )}

        {explainerVideo && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
            <div
              className="absolute inset-0 bg-slate-900/15 backdrop-blur-[3px]"
              onClick={() => setExplainerVideo(null)}
            />
            <div className="rounded-lg w-full max-w-4xl relative z-10 overflow-hidden" style={FROSTED_PANEL_STYLE}>
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{explainerVideo.title} Explainer</h3>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">Learn how to maximize this metric type</p>
                </div>
                <button
                  type="button"
                  onClick={() => setExplainerVideo(null)}
                  className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  aria-label="Close explainer"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-5 h-5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <div className="aspect-video bg-black relative group">
                <iframe
                  width="100%"
                  height="100%"
                  src={explainerVideo.url}
                  title="Video explainer"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="p-6 bg-gray-50 text-center">
                <p className="text-[10px] text-gray-400 font-medium ">
                  [ Placeholder: Official GameMyBiz Explainer Asset ]
                </p>
              </div>
            </div>
          </div>
        )}

        {!isFinalizing && !showSuccess && (
        <>
        <header className="px-[20px] py-[14px] border-b border-[rgba(15,23,42,0.08)] flex items-center justify-between flex-shrink-0 bg-white/55 backdrop-blur-md">
          <div className="flex items-center gap-[11px] min-w-0">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center shadow-sm border border-gray-100 text-gray-700 flex-shrink-0">{metric.unit === "currency" ? (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>) : (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>)}</div>
            <div className="min-w-0">
              <div className="flex items-center gap-[7px]">
                <h2 className="text-[15px] font-semibold text-gray-900 truncate">{metric.name}</h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${isGamified ? "bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]" : "bg-gray-50 text-gray-400"}`}>{isGamified ? gameConfig.type : "not gamified"}</span>
              </div>
              <p className="text-gray-400 text-[11px] font-medium">Appearance &amp; logic designer</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-lg bg-[rgba(15,23,42,0.04)] flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="w-4 h-4"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </header>

        <div className="flex-grow overflow-hidden flex flex-col relative">
          {activeTab === "appearance" && (
            <div className="flex flex-grow overflow-hidden">
              <div className="w-[320px] flex-shrink-0 border-r border-gray-100 overflow-y-auto p-[16px] bg-white flex flex-col gap-[18px]">
                <div>
                  <label className="text-[11px] font-medium text-gray-400 block mb-[9px]">Visualization type</label>
                  <div className="grid grid-cols-3 gap-[7px]">
                    {VIS_TYPES.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => updateVisualConfig({ type: v.id })}
                        className={`rounded-[10px] border transition-all flex flex-col items-center gap-[5px] px-[6px] pt-[9px] pb-[7px] ${visualConfig.type === v.id ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]" : "border-gray-100 bg-white text-gray-400 hover:border-gray-200"}`}
                      >
                        <span className="h-[22px] flex items-center justify-center">{v.preview}</span>
                        <span className="text-[9.5px] font-medium">{v.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {["sparkline", "line", "area", "bar"].includes(visualConfig.type) && (
                  <div>
                    <label className="text-[11px] font-medium text-gray-400 block mb-[9px]">Chart data view</label>
                    <div className="grid grid-cols-2 gap-[7px]">
                      <button
                        type="button"
                        onClick={() => updateVisualConfig({ chartGranularity: "day" })}
                        className={`rounded-[10px] border transition-all flex flex-col items-center gap-[2px] p-[10px] ${visualConfig.chartGranularity === "day" ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]" : "border-gray-100 bg-white text-gray-600 hover:border-gray-200"}`}
                      >
                        <span className="text-[11px] font-medium">Day</span>
                        <span className="text-[9px] text-gray-400">All days of month</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateVisualConfig({ chartGranularity: "week" })}
                        className={`rounded-[10px] border transition-all flex flex-col items-center gap-[2px] p-[10px] ${visualConfig.chartGranularity === "week" ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]" : "border-gray-100 bg-white text-gray-600 hover:border-gray-200"}`}
                      >
                        <span className="text-[11px] font-medium">Week</span>
                        <span className="text-[9px] text-gray-400">4 weeks aggregated</span>
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-[11px] font-medium text-gray-400 block mb-[9px]">Chart style &amp; color</label>
                  <div className="flex flex-wrap gap-[8px] mb-[12px]">
                    {COLORS.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => updateVisualConfig({ primaryColor: c.value })}
                        className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${visualConfig.primaryColor === c.value ? "border-gray-900" : "border-transparent"}`}
                        style={{ backgroundColor: c.value }}
                        title={c.label}
                        aria-label={`Primary color ${c.label}`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-[8px] mb-[12px]">
                    <div className="flex items-center justify-between px-[11px] py-[9px] bg-gray-50 rounded-[9px] border border-gray-100">
                      <span className="text-[11px] font-medium text-gray-600">Gradient</span>
                      <button type="button" onClick={() => updateVisualConfig({ gradientEnabled: !visualConfig.gradientEnabled })} className={`w-8 h-4 rounded-full relative transition-colors ${visualConfig.gradientEnabled ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`} aria-label={visualConfig.gradientEnabled ? "Gradient on" : "Gradient off"}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${visualConfig.gradientEnabled ? "translate-x-[17px]" : "translate-x-0.5"}`} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-[11px] py-[9px] bg-gray-50 rounded-[9px] border border-gray-100">
                      <span className="text-[11px] font-medium text-gray-600">Smooth</span>
                      <button type="button" onClick={() => updateVisualConfig({ smoothLine: !visualConfig.smoothLine })} className={`w-8 h-4 rounded-full relative transition-colors ${visualConfig.smoothLine ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`} aria-label={visualConfig.smoothLine ? "Smooth on" : "Smooth off"}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${visualConfig.smoothLine ? "translate-x-[17px]" : "translate-x-0.5"}`} />
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] font-medium text-gray-500 mb-[7px]">Opacity</p>
                  <input type="range" min="0.1" max="1.0" step="0.1" value={visualConfig.chartOpacity} onChange={(e) => updateVisualConfig({ chartOpacity: parseFloat(e.target.value) })} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[var(--gmb-primary)]" aria-label="Chart opacity" />
                </div>
                <button type="button" onClick={() => setVisualConfig(DEFAULT_VISUAL_CONFIG)} className="text-[11px] font-medium text-gray-400 hover:text-[var(--gmb-primary)] transition-colors self-start">
                  Restore defaults
                </button>
              </div>
              <div className="flex-grow flex flex-col p-[20px] overflow-hidden" style={{ background: "#f5f6f8", backgroundImage: "radial-gradient(#dde2e8 1px, transparent 1px)", backgroundSize: "14px 14px" }}>
                <span className="text-[11px] font-medium text-gray-400 bg-white border border-gray-100 px-[12px] py-[5px] rounded-full self-start">Live preview</span>
                <div className="flex-grow flex items-center justify-center">
                  <div className="w-[260px]">
                  <CompanyMetricCard
                    metric={(() => {
                      const base = {
                        ...metric,
                        visualConfig: { ...(metric.visualConfig || {}), ...visualConfig },
                        appearance: visualConfig.type,
                      };
                      const isChartType = ["sparkline", "line", "area", "bar"].includes(visualConfig.type);
                      if (isChartType && companyScoresByDay && metric.id) {
                        const granularity = visualConfig.chartGranularity === "week" ? "week" : "day";
                        const sparklineData = getCompanyMetricChartData(companyScoresByDay, metric.id, companyToday().startOf("month"), granularity);
                        if (Array.isArray(sparklineData) && sparklineData.length > 0) {
                          return { ...base, sparklineData };
                        }
                      }
                      return base;
                    })()}
                    isGlobalView={true}
                    isPreview
                  />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "gamification" && (() => {
            if (isGlobalView) return renderGamificationStatusPanel();
            return (
            <div className="flex-grow flex flex-col overflow-hidden relative bg-[#F9FBFC]">
              {(() => {
                if (isGamified) return null;
                if (!resolvedWorkspace) {
                  return (
                    <div className="flex-grow flex flex-col items-center justify-center gap-3">
                      <LoadingScreen height="auto" logoHeight="160px" />
                      <p className="text-gray-400 font-medium text-sm tracking-wide">Resolving workspace context...</p>
                    </div>
                  );
                }
                return (
                  <div className="flex-grow flex flex-col items-center justify-center p-12 text-center">
                    <PremiumRobotIcon className="w-24 h-24 mb-10 shadow-md shadow-[var(--gmb-primary-soft)] rounded-lg bg-white p-2" />
                    <div className="flex items-center gap-2 mb-6 px-4 py-1.5 bg-white rounded-full border border-gray-100 shadow-sm">
                      <div className="w-5 h-5 rounded-full overflow-hidden border border-gray-200">
                        <img src={resolvedWorkspace.icon} alt={resolvedWorkspace.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-[11px] font-medium text-gray-900">{resolvedWorkspace.name}</span>
                      <span className="w-1 h-1 bg-gray-300 rounded-full mx-1" />
                      <span className="text-[10px] font-medium text-[var(--gmb-primary)] ">Active Workspace</span>
                    </div>
                    {isContextShifted ? (
                      <>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4 tracking-tight">You're now in {resolvedWorkspace.name}</h3>
                        <p className="text-gray-500 font-medium mb-10 leading-relaxed max-w-lg mx-auto text-sm">You're about to gamify this metric for the <span className="text-[var(--gmb-primary)] font-medium">{resolvedWorkspace.name}</span> team.</p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4 tracking-tight">Gamify this metric</h3>
                        <p className="text-gray-500 font-medium mb-10 leading-relaxed max-w-lg mx-auto text-sm">Turn this metric into a game to track performance, reward players, and power leaderboards.</p>
                      </>
                    )}
                    <div className="flex flex-col items-center gap-6">
                      <button type="button" onClick={() => setIsGamified(true)} className={primaryBtnClass + " px-14 py-4 text-base"}>Start gamifying this metric</button>
                      {onReopenWorkspaceSelector && isContextShifted && (
                        <button type="button" onClick={() => onReopenWorkspaceSelector(metric)} className="text-[11px] font-medium text-gray-400 hover:text-gray-900 transition-colors pb-1 border-b border-transparent hover:border-gray-200">
                          Choose a different workspace
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
              {isGamified && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="flex-shrink-0 pt-10 pb-6 bg-white/50 backdrop-blur-sm z-20 border-b border-gray-100/50">
                    <div className="flex items-center justify-center space-x-5">
                      {[1, 2, 3].map((s) => (
                        <div key={s} className="flex items-center">
                          <div className={(() => { if (gameStep === s) return "w-10 h-10 rounded-[12px] flex items-center justify-center font-medium text-xs transition-all bg-[var(--gmb-primary)] text-white shadow-lg"; if (gameStep > s) return "w-10 h-10 rounded-[12px] flex items-center justify-center font-medium text-xs transition-all bg-[var(--gmb-primary-tint)] text-[var(--gmb-primary)]"; return "w-10 h-10 rounded-[12px] flex items-center justify-center font-medium text-xs transition-all bg-gray-100 text-gray-400"; })()}>
                            {gameStep > s ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-4 h-4"><polyline points="20 6 9 17 4 12" /></svg> : `0${s}`}
                          </div>
                          {s < 3 && <div className={`w-14 h-0.5 mx-2 rounded-full ${gameStep > s ? "bg-[var(--gmb-primary-tint)]" : "bg-gray-200"}`} />}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-grow overflow-y-auto px-10 py-12 scroll-smooth">
                    <div className="w-full max-w-3xl mx-auto flex flex-col min-h-full">
                      {gameStep === 1 && (
                        <div className="space-y-10">
                          <div className="text-center mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2 tracking-tight ">Choose your metric type</h3>
                            <p className="text-gray-500 font-normal text-sm">Select how this metric should behave as a game.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto w-full">
                            {["progressive", "anchor"].map((type) => (
                              <div key={type} className={`p-8 rounded-lg border-2 text-left bg-white transition-all flex flex-col ${gameConfig.type === type ? "border-[var(--gmb-primary)] shadow-md" : "border-gray-100"}`}>
                                <div className="mb-6 text-[var(--gmb-primary)]">{type === "progressive" ? (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>) : (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7"><circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>)}</div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2 capitalize">{type}</h4>
                                <p className="text-gray-500 text-xs font-normal leading-relaxed mb-6">{type === "progressive" ? "Track continuous progress towards a milestone." : "Hold a specific value or threshold over time."}</p>
                                <div className="mt-auto">
                                  <button type="button" onClick={() => setGameConfig((prev) => ({ ...prev, type }))} className={`w-full py-2.5 rounded-xl font-medium text-xs transition-all mb-3 ${gameConfig.type === type ? "bg-[var(--gmb-primary)] text-white shadow-lg" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}>Select</button>
                                  <button
                                    type="button"
                                    onClick={() => setExplainerVideo({ title: type === "progressive" ? "Progressive" : "Anchor", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" })}
                                    className="w-full text-center text-[10px] font-medium text-[var(--gmb-primary)] hover:text-[var(--gmb-primary)] transition-colors"
                                  >
                                    Watch explainer
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {gameStep === 2 && gameConfig.type === "progressive" && (
                        <div className="max-w-2xl mx-auto space-y-10 w-full">
                          <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2 tracking-tight ">Set targets and point rules</h3>
                            <p className="text-gray-500 font-normal text-sm">Configure your core gameplay logic for {metric.name}.</p>
                          </div>
                          <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm space-y-8">
                            <section>
                              <label className="text-[11px] font-medium text-gray-400 block mb-2">Game name (player-facing)</label>
                              <input type="text" value={gameConfig.gameName ?? ""} placeholder="e.g. Sales Rockstar Challenge" onChange={(e) => setGameConfig((prev) => ({ ...prev, gameName: e.target.value }))} className={inputBaseClass} />
                            </section>
                            <section>
                              <label className="text-[11px] font-medium text-gray-400 block mb-2">Daily target</label>
                              <div className="relative">
                                <input type="number" value={gameConfig.dailyTarget ?? ""} placeholder="e.g. 1000" onChange={(e) => {
                                const raw = e.target.value;
                                const num = raw === "" ? undefined : Number(raw);
                                setGameConfig((prev) => ({ ...prev, dailyTarget: num }));
                              }} className={inputBaseClass + " pr-20"} />
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-medium text-gray-300 ">Goal</span>
                              </div>
                              <p className="text-[10px] text-gray-400 mt-2">This value becomes the primary display on your metric card.</p>
                            </section>
                            <section>
                              <label className="text-[11px] font-medium text-gray-400 block mb-3">Points rule</label>
                              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-4">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className="text-sm font-medium text-gray-600">If a player achieves</span>
                                  <input type="number" value={gameConfig.pointsRule?.metricAmount ?? ""} placeholder="1" onChange={(e) => {
                                  const raw = e.target.value;
                                  const num = raw === "" ? 1 : (Number(raw) || 1);
                                  setGameConfig((prev) => ({ ...prev, pointsRule: { ...(prev.pointsRule || {}), metricAmount: num } }));
                                }} className="w-20 bg-white border border-gray-200 p-2 rounded-xl text-center text-sm font-medium text-gray-900 focus:border-[var(--gmb-primary)] outline-none" />
                                  <span className="text-sm font-medium text-[var(--gmb-primary)]">{gameConfig.gameName}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-medium text-gray-600">They earn</span>
                                  <input type="number" value={gameConfig.pointsRule?.pointsEarned ?? ""} placeholder="10" onChange={(e) => {
                                  const raw = e.target.value;
                                  const num = raw === "" ? 10 : (Number(raw) || 10);
                                  setGameConfig((prev) => ({ ...prev, pointsRule: { ...(prev.pointsRule || {}), pointsEarned: num } }));
                                }} className="w-20 bg-[var(--gmb-primary-soft)] border border-[var(--gmb-primary-tint)] p-2 rounded-xl text-center text-sm font-medium text-[var(--gmb-primary)] outline-none" />
                                  <span className="text-sm font-medium text-gray-600">points</span>
                                </div>
                              </div>
                            </section>
                          </div>
                        </div>
                      )}
                      {gameStep === 2 && gameConfig.type === "anchor" && (
                        <div className="max-w-2xl mx-auto space-y-10 w-full">
                          <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2 tracking-tight ">Metric Goal Setup</h3>
                            <p className="text-gray-500 font-normal text-sm leading-relaxed">Where your metric pulls info from</p>
                          </div>
                          <div className="bg-white p-10 rounded-xl border border-gray-50 shadow-sm space-y-10">
                            <section>
                              <div className="flex items-center gap-3 mb-4">
                                <span className="text-[var(--gmb-primary)]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></span>
                                <label className="text-sm font-medium text-gray-900 tracking-tight">Name your Metric</label>
                              </div>
                              <input type="text" value={gameConfig.gameName ?? ""} placeholder="Metric Name" onChange={(e) => setGameConfig((prev) => ({ ...prev, gameName: e.target.value }))} className={inputBaseClass} />
                              <p className="text-[11px] text-gray-400 mt-3 font-medium">Give your metric a clear name so players know what they're working toward.</p>
                            </section>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              <section>
                                <div className="flex items-center gap-3 mb-4">
                                  <span className="text-[var(--gmb-primary)]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></svg></span>
                                  <label className="text-sm font-medium text-gray-900 tracking-tight">Set Your Anchor (Benchmark)</label>
                                </div>
                                <div className="flex gap-0 rounded-lg overflow-hidden border border-gray-200 focus-within:border-[var(--gmb-primary)] transition-all">
                                  {/* Keep "" while the field is cleared — coercing to Number turned an
                                      empty field into a sticky leading 0 ("085"). Save coerces later. */}
                                  <input type="number" value={gameConfig.anchorValue ?? 50} onChange={(e) => setGameConfig((prev) => ({ ...prev, anchorValue: e.target.value === "" ? "" : Number(e.target.value) }))} className="flex-grow bg-gray-50 px-5 py-3 font-normal text-gray-900 focus:bg-white outline-none" />
                                  <select value={gameConfig.anchorUnit ?? "percent"} onChange={(e) => setGameConfig((prev) => ({ ...prev, anchorUnit: e.target.value }))} className="bg-gray-50 border-l border-gray-200 px-4 py-3 font-medium text-[var(--gmb-primary)] outline-none hover:bg-white transition-all cursor-pointer">
                                    <option value="percent">%</option>
                                    <option value="currency">$</option>
                                    <option value="number">#</option>
                                  </select>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-3 font-medium">This is the benchmark players will try to hit.</p>
                              </section>
                              <section>
                                <div className="flex items-center gap-3 mb-4">
                                  <span className="text-[var(--gmb-primary)]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><rect x="2" y="7" width="20" height="10" rx="1"/><line x1="7" y1="7" x2="7" y2="11"/><line x1="12" y1="7" x2="12" y2="11"/><line x1="17" y1="7" x2="17" y2="11"/></svg></span>
                                  <label className="text-sm font-medium text-gray-900 tracking-tight">Set Unit Value</label>
                                </div>
                                <input type="number" value={1} readOnly className={inputBaseClass + " opacity-50 cursor-not-allowed"} />
                                <p className="text-[11px] text-gray-400 mt-3 font-medium">The unit of the scale.</p>
                              </section>
                            </div>
                            <section className="bg-gray-50/50 p-8 rounded-xl border border-gray-100 space-y-8">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-[var(--gmb-primary)]"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></span>
                                <label className="text-sm font-medium text-gray-900 tracking-tight">Define how performance translates to points</label>
                              </div>
                              <div className="space-y-4">
                                <label className="text-[11px] font-medium text-gray-400 pl-1">Benchmark point</label>
                                <input type="number" value={gameConfig.benchmarkPoints ?? 1000} onChange={(e) => setGameConfig((prev) => ({ ...prev, benchmarkPoints: e.target.value === "" ? "" : Number(e.target.value) }))} className="w-full bg-white border border-gray-100 px-5 py-3 rounded-lg font-medium text-gray-900 focus:border-[var(--gmb-primary)] outline-none shadow-sm" />
                                <p className="text-center text-[11px] font-medium text-pink-500 mt-4 italic ">How many points is deducted or added to player</p>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-1 bg-pink-500 rounded-full" />
                                    <label className="text-[11px] font-medium text-gray-900 tracking-tight">Point lost per unit below the Goal</label>
                                  </div>
                                  <input type="number" value={gameConfig.pointsPerUnitBelow ?? 25} onChange={(e) => setGameConfig((prev) => ({ ...prev, pointsPerUnitBelow: e.target.value === "" ? "" : Number(e.target.value) }))} className="w-full bg-white border border-gray-100 px-5 py-3 rounded-lg font-medium text-gray-900 focus:border-[var(--gmb-primary)] outline-none shadow-sm" />
                                </div>
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-1 bg-emerald-500 rounded-full" />
                                    <label className="text-[11px] font-medium text-gray-900 tracking-tight">Point gains per unit Above the Goal</label>
                                  </div>
                                  <input type="number" value={gameConfig.pointsPerUnitAbove ?? 25} onChange={(e) => setGameConfig((prev) => ({ ...prev, pointsPerUnitAbove: e.target.value === "" ? "" : Number(e.target.value) }))} className="w-full bg-white border border-gray-100 px-5 py-3 rounded-lg font-medium text-gray-900 focus:border-[var(--gmb-primary)] outline-none shadow-sm" />
                                </div>
                              </div>
                            </section>
                            <div className="text-center p-6 bg-[var(--gmb-primary-soft)] rounded-xl border border-[var(--gmb-primary-tint)]">
                              <p className="text-xs font-medium text-gray-600 leading-relaxed">
                                At <span className="font-medium text-gray-900">{gameConfig.anchorValue}{{ percent: "%", currency: "$" }[gameConfig.anchorUnit] ?? ""}</span>, players have <span className="font-medium text-[var(--gmb-primary)]">{gameConfig.benchmarkPoints} points</span>. <br />
                                For every 1 unit above, they gain <span className="font-medium text-emerald-600">{gameConfig.pointsPerUnitAbove} points</span>. <br />
                                For every 1 unit below, they lose <span className="font-medium text-pink-600">{gameConfig.pointsPerUnitBelow} points</span>.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {gameStep === 3 && (
                        <div className="max-w-2xl mx-auto space-y-8 w-full pb-8">
                          <div className="text-center">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2 tracking-tight ">FINALIZE BEHAVIOR</h3>
                            <p className="text-gray-500 font-normal text-sm leading-relaxed">Review settings and define performance behavior.</p>
                          </div>
                          <div className="bg-white p-8 sm:p-10 rounded-xl border border-gray-50 shadow-sm space-y-10">
                            <div className="space-y-4">
                              <label className="text-[11px] font-medium text-gray-400 pl-1">EFFECT TYPE</label>
                              <div className="bg-gray-50 p-2 rounded-xl flex gap-2 border border-gray-100/50">
                                {["positive", "negative"].map((eff) => (
                                  <button key={eff} type="button" onClick={() => setGameConfig((prev) => ({ ...prev, effectType: eff }))} className={`flex-grow py-4 px-6 rounded-lg transition-all flex flex-col items-center justify-center text-center ${gameConfig.effectType === eff ? "bg-white text-gray-900 shadow-sm border border-gray-100" : "text-gray-400 hover:text-gray-600 border border-transparent hover:bg-white/30"}`}>
                                    <span className="text-base font-medium block mb-0.5">{eff === "positive" ? "Positive" : "Negative"}</span>
                                    <span className="text-[11px] font-normal opacity-60 leading-tight">{eff === "positive" ? "Increase is better" : "Decrease is better"}</span>
                                  </button>
                                ))}
                              </div>
                              <p className="text-[11px] text-gray-400 font-medium pl-1 leading-relaxed italic">Determines if increasing the metric value adds points.</p>
                            </div>
                            <div className="space-y-4">
                              <label className="text-[11px] font-medium text-gray-400 pl-1">VALUE TYPE</label>
                              <div className="bg-gray-50 p-2 rounded-xl flex gap-2 border border-gray-100/50">
                                {["monetary", "item"].map((val) => (
                                  <button key={val} type="button" onClick={() => setGameConfig((prev) => ({ ...prev, valueType: val }))} className={`flex-grow py-4 px-6 rounded-lg transition-all flex flex-col items-center justify-center text-center ${gameConfig.valueType === val ? "bg-white text-gray-900 shadow-sm border border-gray-100" : "text-gray-400 hover:text-gray-600 border border-transparent hover:bg-white/30"}`}>
                                    <span className="text-base font-medium block mb-0.5">{val === "monetary" ? "Monetary" : "Unit / Count"}</span>
                                    <span className="text-[11px] font-normal opacity-60 leading-tight">{val === "monetary" ? "Tracking $ value" : "Tracks raw units"}</span>
                                  </button>
                                ))}
                              </div>
                              <p className="text-[11px] text-gray-400 font-medium pl-1 leading-relaxed italic">Defines if calculations use currency or raw units.</p>
                            </div>
                            <div className="pt-6 border-t border-gray-50">
                              <button
                                type="button"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="flex items-center justify-between w-full px-6 py-4 bg-gray-50/50 hover:bg-gray-50 rounded-lg transition-all border border-gray-100 group"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-500"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></span>
                                  <span className="text-sm font-medium text-gray-700">Advanced system settings</span>
                                </div>
                                <div className={`transition-transform duration-300 ${showAdvanced ? "rotate-180" : ""}`}>
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-4 h-4 text-gray-400"><path d="M19 9l-7 7-7-7"/></svg>
                                </div>
                              </button>
                              {showAdvanced && (
                                <div className="mt-4 p-8 bg-white border border-gray-50 rounded-xl shadow-inner space-y-8">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h5 className="text-[13px] font-semibold text-gray-900">Composite metric (Workspace Only)</h5>
                                      <p className="text-[11px] text-gray-400 font-medium mt-0.5">Allows this metric to be calculated from multiple sources (Workspace Only)</p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => !isGlobalView && setGameConfig((prev) => ({ ...prev, compositeMetric: !prev.compositeMetric }))}
                                      className={`w-10 h-6 rounded-full relative transition-colors ${gameConfig.compositeMetric ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`}
                                      aria-label="Toggle composite metric"
                                    >
                                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${gameConfig.compositeMetric ? "translate-x-5" : "translate-x-1"}`} />
                                    </button>
                                  </div>
                                  {gameConfig.compositeMetric && !isGlobalView && (
                                    <div className="mt-6 p-6 bg-gray-50/50 rounded-xl border border-gray-100 space-y-6">
                                      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-end">
                                        <div className="space-y-2">
                                          <label className="text-[11px] font-medium text-gray-500 block ml-1">Select workspace metric</label>
                                          <div className="relative">
                                            <select
                                              value={gameConfig.compositeConfig?.metricAId ?? ""}
                                              onChange={(e) => updateCompositeConfig({ metricAId: e.target.value })}
                                              className="w-full bg-white border border-gray-200 p-3.5 rounded-xl text-xs font-medium outline-none focus:border-[var(--gmb-primary)] focus:ring-2 focus:ring-[var(--gmb-primary)]/10 transition-all appearance-none cursor-pointer"
                                            >
                                              <option value="">Select Metric A</option>
                                              {workspaceMetrics.map((m) => (
                                                <option key={m.dashboardId || m.id} value={m.id}>{m.name}</option>
                                              ))}
                                            </select>
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3.5 h-3.5"><path d="M19 9l-7 7-7-7"/></svg>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="space-y-2 w-20">
                                          <label className="text-[11px] font-medium text-gray-500 block text-center">Calculation</label>
                                          <div className="relative">
                                            <select
                                              value={gameConfig.compositeConfig?.operator ?? "add"}
                                              onChange={(e) => updateCompositeConfig({ operator: e.target.value })}
                                              className="w-full bg-white border border-gray-200 p-3.5 rounded-xl text-xs font-medium outline-none focus:border-[var(--gmb-primary)] focus:ring-2 focus:ring-[var(--gmb-primary)]/10 transition-all text-center appearance-none cursor-pointer"
                                            >
                                              {CALCULATION_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                              ))}
                                            </select>
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3"><path d="M19 9l-7 7-7-7"/></svg>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="space-y-2">
                                          <label className="text-[11px] font-medium text-gray-500 block ml-1">Select workspace metric</label>
                                          <div className="relative">
                                            <select
                                              value={gameConfig.compositeConfig?.metricBId ?? ""}
                                              onChange={(e) => updateCompositeConfig({ metricBId: e.target.value })}
                                              className="w-full bg-white border border-gray-200 p-3.5 rounded-xl text-xs font-medium outline-none focus:border-[var(--gmb-primary)] focus:ring-2 focus:ring-[var(--gmb-primary)]/10 transition-all appearance-none cursor-pointer"
                                            >
                                              <option value="">Select Metric B</option>
                                              {workspaceMetrics
                                                .filter((m) => m.id !== gameConfig.compositeConfig?.metricAId)
                                                .map((m) => (
                                                  <option key={m.dashboardId || m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </select>
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3.5 h-3.5"><path d="M19 9l-7 7-7-7"/></svg>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-between pt-2">
                                        <div className="flex-grow">
                                          {(!gameConfig.compositeConfig?.metricAId || !gameConfig.compositeConfig?.metricBId) && (
                                            <p className="text-[10px] text-amber-500 font-medium italic animate-pulse">Please select both metrics to complete the formula.</p>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          disabled={isCompositeIncomplete}
                                          onClick={handleSaveComposite}
                                          className={`px-5 py-2.5 rounded-xl text-[11px] font-medium transition-all ${isCompositeIncomplete ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200" : "bg-[var(--gmb-primary)] text-white hover:bg-[var(--gmb-primary-hover)] shadow-sm hover:shadow-md active:scale-95 border border-transparent"}`}
                                        >
                                          Save composite
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h5 className="text-[13px] font-semibold text-gray-900">Monitor mode</h5>
                                      <p className="text-[11px] text-gray-400 font-medium mt-0.5">Metric is tracked but does not award points</p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setGameConfig((prev) => ({ ...prev, monitorMode: !prev.monitorMode }))}
                                      className={`w-10 h-6 rounded-full relative transition-colors ${gameConfig.monitorMode ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`}
                                      aria-label="Toggle monitor mode"
                                    >
                                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${gameConfig.monitorMode ? "translate-x-5" : "translate-x-1"}`} />
                                    </button>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h5 className="text-[13px] font-semibold text-gray-900">Scorekeeper only</h5>
                                      <p className="text-[11px] text-gray-400 font-medium mt-0.5">Admin-entered in Scorekeeper — hidden from public boards, points still count</p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setGameConfig((prev) => ({ ...prev, scorekeeperOnly: !prev.scorekeeperOnly }))}
                                      className={`w-10 h-6 rounded-full relative transition-colors ${gameConfig.scorekeeperOnly ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`}
                                      aria-label="Toggle scorekeeper only"
                                    >
                                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${gameConfig.scorekeeperOnly ? "translate-x-5" : "translate-x-1"}`} />
                                    </button>
                                  </div>
                                  <div className="space-y-3 pt-4 border-t border-gray-50">
                                    <label className="text-[11px] font-medium text-gray-400 ">Reset frequency</label>
                                    <select
                                      value={gameConfig.resetFrequency ?? "daily"}
                                      onChange={(e) => setGameConfig((prev) => ({ ...prev, resetFrequency: e.target.value }))}
                                      className="w-full bg-gray-50 border border-gray-100 p-3.5 rounded-xl text-sm font-medium outline-none focus:border-[var(--gmb-primary)] transition-all"
                                    >
                                      <option value="daily">Daily Reset</option>
                                      <option value="weekly">Weekly Reset</option>
                                      <option value="monthly">Monthly Reset</option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
          })()}

          {activeTab === "advanced" && (
            <div className="flex-grow flex flex-col items-center justify-center p-12 bg-[#F9FBFC] overflow-y-auto">
              <div className="w-full max-w-lg space-y-8">
                <div className="bg-white p-10 rounded-xl shadow-md border border-gray-100 space-y-8">
                  <h3 className="text-base font-semibold text-gray-900 tracking-tight">Advanced developer tools</h3>
                  <p className="text-sm text-gray-500 font-normal">System-level configurations for this metric on GameMyBiz.</p>
                  <div className="p-6 bg-gray-50 rounded-xl border border-gray-100 font-mono text-xs opacity-60">
                    metric_id: {metric.dashboardId}
                    <br />
                    app_id: {metric.appId}
                    <br />
                    gamified: {isGamified ? "true" : "false"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="px-[20px] py-[14px] border-t border-[rgba(15,23,42,0.08)] bg-white/55 backdrop-blur-md flex items-center justify-between flex-shrink-0 z-[100]">
          <button type="button" onClick={onClose} className={textBtnClass}>Cancel</button>
          <div className="flex items-center space-x-4">
            {activeTab === "gamification" && isGamified && !showSuccess && !isFinalizing && !isGlobalView && (
              <>
                {gameStep > 1 && <button type="button" onClick={() => setGameStep(gameStep - 1)} className={secondaryBtnClass}>Back</button>}
                <button
                  type="button"
                  onClick={() => (gameStep < 3 ? setGameStep(gameStep + 1) : handleLaunchOrUpdate())}
                  disabled={gameStep === 3 && isCompositeIncomplete}
                  className={primaryBtnClass}
                >
                  {(() => { if (gameStep < 3) return "Next"; if (isAlreadyLive) return "Update challenge"; return "Launch game"; })()}
                </button>
              </>
            )}
            {activeTab === "appearance" && (
              <button
                type="button"
                onClick={() => { handleSave(undefined, false); setShowAppearanceSuccess(true); }}
                className={primaryBtnClass}
              >
                Save appearance
              </button>
            )}
            {activeTab === "advanced" && <button type="button" onClick={() => handleSave()} className={primaryBtnClass}>Save changes</button>}
          </div>
        </footer>
        </>
        )}
      </div>
    </div>
  );
}

export default MetricSettingsModal;
